import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

import { PropertyService } from '../../../_services/properties/property.service';
import {
  InventoryProperty,
  PropertyApiRecord,
  toInventoryProperty,
} from '../models/ci-property.model';
import { PropertyCategory } from '../models/ci-form.model';
import { formatCompactCurrency } from '../../../core/utils/format.util';

const MIN_TERM_LENGTH = 2;
let searchId = 0;

const normalise = (value: string | null | undefined): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Shared CI property search with a floating, dismissible results list. */
@Component({
  selector: 'ci-property-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
  templateUrl: './ci-property-search.component.html',
})
export class CiPropertySearchComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  readonly resultsOpen = signal(false);

  private readonly properties = inject(PropertyService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchInputId = `ci-property-search-${searchId++}`;
  readonly selectedId = input<string | null>(null);
  /** Primary CI accepts every property; other CI types narrow inventory search. */
  readonly category = input<PropertyCategory>('primary-project');
  /** When used for comparable benchmarking, keep candidates in this market. */
  readonly microMarket = input<string>('');
  readonly picked = output<InventoryProperty>();

  readonly term = new FormControl('', { nonNullable: true });

  readonly results = signal<InventoryProperty[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** True once a search has actually run, so "no results" never shows on load. */
  readonly searched = signal(false);

  compact = formatCompactCurrency;

  constructor() {
    this.term.valueChanges
      .pipe(
        tap((value) => this.resultsOpen.set(value.trim().length >= MIN_TERM_LENGTH)),
        map((value) => value.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.error.set(null);
          if (value.length < MIN_TERM_LENGTH) {
            this.results.set([]);
            this.searched.set(false);
            this.loading.set(false);
          } else {
            this.loading.set(true);
          }
        }),
        switchMap((value) =>
          value.length < MIN_TERM_LENGTH
            ? of(null)
            : this.properties.searchProperty(value).pipe(
                map((res) => this.toResults(res)),
                catchError((err: { message?: string }) => {
                  this.error.set(err?.message || 'Property search failed. Please try again.');
                  return of([] as InventoryProperty[]);
                }),
              ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        if (items === null) return;
        this.results.set(items);
        this.searched.set(true);
        this.loading.set(false);
      });
  }

  selectProperty(property: InventoryProperty): void {
    this.resultsOpen.set(false);
    this.term.setValue(property.propertyName, { emitEvent: false });
    this.picked.emit(property);
  }

  @HostListener('document:pointerdown', ['$event'])
  closeOutside(event: Event): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.resultsOpen.set(false);
  }

  @HostListener('focusout', ['$event'])
  closeOnBlur(event: FocusEvent): void {
    if (!this.host.nativeElement.contains(event.relatedTarget as Node | null)) this.resultsOpen.set(false);
  }

  clear(): void {
    this.term.setValue('');
  }

  visibleResults(): InventoryProperty[] {
    return this.results().filter((item) => this.matchesMarket(item) && this.matchesCategory(item));
  }

  /**
   * Loose micro-market match: search records may spell the market differently
   * ("Whitefield (C2)", extra spaces) or omit it, so compare normalised names
   * against the market, cluster and location, and keep records with no market.
   */
  private matchesMarket(item: InventoryProperty): boolean {
    const market = normalise(this.microMarket().replace(/\(C\d+\)/g, ''));
    if (!market) return true;
    const candidates = [item.microMarket, item.cluster, item.location]
      .map((value) => normalise(value.replace(/\(C\d+\)/g, '')))
      .filter(Boolean);
    return !candidates.length || candidates.some((value) => value.includes(market) || market.includes(value));
  }

  private matchesCategory(item: InventoryProperty): boolean {
    const category = this.category();
    if (category === 'primary-project') return true;

    const type = `${item.propertyType} ${item.listingType} ${item.propertyStructureTypes.join(' ')}`.toLowerCase();
    // Whole-word checks, so "Penthouse" or "Farmhouse" don't count as a house.
    const has = (word: string) => new RegExp(`\\b${word}\\b`).test(type);
    switch (category) {
      case 'resale':
        // Older inventory records often omit listingType. In that case, unit
        // properties are the valid resale candidates; only land/house records
        // must be excluded from this tab.
        return has('resale') || (!has('plot') && !has('land') && !has('house'));
      case 'plot-land':
        return has('plot') || has('land');
      case 'independent-house':
        return has('house') || has('villa');
      default:
        return true;
    }
  }

  priceRange(item: InventoryProperty): string {
    if (item.priceMin === null) return '';
    return item.priceMax !== null && item.priceMax !== item.priceMin
      ? `${this.compact(item.priceMin)} – ${this.compact(item.priceMax)}`
      : this.compact(item.priceMin);
  }

  subtitle(item: InventoryProperty): string {
    const area = [item.microMarket, item.cluster].filter(Boolean).join(' · ');
    return area || item.location;
  }

  private toResults(res: { data?: PropertyApiRecord[] } | null): InventoryProperty[] {
    return (res?.data ?? []).map(toInventoryProperty);
  }
}
